import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, withModifiers as _withModifiers, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-358d3bf9"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "availability-search" }
const _hoisted_2 = { class: "availability-search__body" }
const _hoisted_3 = { class: "availability-search__period" }
const _hoisted_4 = { class: "availability-search__title" }
const _hoisted_5 = { class: "availability-search__date-controls" }
const _hoisted_6 = { class: "availability-search__group" }
const _hoisted_7 = { class: "availability-search__title" }
const _hoisted_8 = { class: "availability-search__group-inputs" }

import DatePicker from '@/components/DatePicker.vue'
import { computed, PropType, reactive, ref, watch } from 'vue'
import { PublicServiceDetails } from '@/models/public/service.model'
import format from 'date-fns/format'
import {
  GroupAvailability,
  IGroupAvailabilitySearch,
  IGroupAvailabilitySearchPayload,
} from '@/models/public/order.model'
import { getValidationErrorData } from '@/utils/errors'
import { watchEffect } from 'vue'
import startOfToday from 'date-fns/startOfToday'
import BaseNumberIncrementer from '@/components/BaseNumberIncrementer.vue'
import axios from 'axios'
import isBefore from 'date-fns/isBefore'
import { StringDate } from '@/models/shared.model'
import { debounce, List } from 'lodash'
import parseISO from 'date-fns/parseISO'
import {
  State,
  initializePickerYearMonth,
  stateFieldMonthYearChange,
} from '@/components/public/housing/HousingResultCardSearch.vue'
import endOfMonth from 'date-fns/endOfMonth'
import { formatServerDate } from '@/utils/formatters'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import isSameDay from 'date-fns/isSameDay'


export default /*#__PURE__*/_defineComponent({
  __name: 'PublicServiceGroupAvailabilitySearch',
  props: {
  modelValue: {
    type: Object as PropType<IGroupAvailabilitySearch | undefined>,
    default: undefined,
  },
  service: {
    type: Object as PropType<PublicServiceDetails>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
},
  emits: ["update:modelValue", "availability"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:modelValue', newValue: IGroupAvailabilitySearch): void
  (e: 'availability', availability: GroupAvailability): void
}), expose: any, slots: any, attrs: any }) {

const props = __props





// We update parent based on these changes
const selectedDate = ref<StringDate | null>(null)
const selectedGroupSize = ref<number>(1)

const availableDates = ref<List[Date]>([])
const isAvailableDatesLoading = ref<boolean>(false)
const availability = ref<GroupAvailability | undefined>(undefined)
const errors = ref<Record<string, string[]> | undefined>(undefined)

const state = reactive<State>({
  // What month is open in datepicker. We use it to fetch and show availability per date.
  periodPickerYearMonth: initializePickerYearMonth(props.modelValue?.start),
})

const selectedStart = computed<StringDate | null>(() => {
  return selectedDate.value
})

const selectedEnd = computed<StringDate | null>(() => {
  return selectedDate.value
})

const selectedDateErrors = computed(() => {
  const _errors = []
  _errors.push(...(errors.value?.start ?? []))
  _errors.push(...(errors.value?.check_in ?? []))
  _errors.push(...(errors.value?.end ?? []))
  _errors.push(...(errors.value?.check_out ?? []))
  return _errors
})

const onPeriodPickerMonthYearChange = stateFieldMonthYearChange(
  state,
  'periodPickerYearMonth',
)

const getPeriodDisabledDates = (date: Date) => {
  if (isAvailableDatesLoading.value) {
    return true
  }
  if (isBefore(date, startOfToday())) {
    return true
  }

  for (const availableDate of availableDates.value) {
    if (isSameDay(date, availableDate)) {
      return false
    }
  }
  return true
}

const updateSelectedDate = (date: Date[] | null) => {
  if (date === null) {
    selectedDate.value = null
  }

  selectedDate.value = format(date, 'yyyy-MM-dd')
}

const searchAvailableDates = async () => {
  const hashId = props.service?.hash_id
  if (!hashId) {
    return
  }

  errors.value = undefined
  isAvailableDatesLoading.value = true
  const startOfMonth: Date = new Date(
    state.periodPickerYearMonth.year,
    state.periodPickerYearMonth.month,
    1,
  )
  const payload: IGroupAvailabilitySearchPayload = {
    start: formatServerDate(startOfMonth),
    end: formatServerDate(endOfMonth(startOfMonth)),
    group_size: selectedGroupSize.value,
  }
  axios
    .post<List[StringDate]>(
      `public/service/${hashId}/group-available-dates/`,
      payload,
    )
    .then((response: { data: List[StringDate] }) => {
      availableDates.value = response.data.map((dateStr: StringDate) =>
        parseISO(dateStr),
      )
    })
    .catch((error) => {
      errors.value = getValidationErrorData(error)
    })
    .finally(() => {
      isAvailableDatesLoading.value = false
    })
}
const debouncedSearchAvailableDates = debounce(searchAvailableDates, 300)

// Also debounced method below
const searchAvailability = async () => {
  const hashId = props.service?.hash_id
  if (!hashId || !selectedStart.value || !selectedEnd.value) {
    return
  }

  errors.value = undefined
  const payload: IGroupAvailabilitySearchPayload = {
    start: selectedStart.value,
    group_size: selectedGroupSize.value,
  }
  axios
    .post<GroupAvailability>(
      `public/service/${hashId}/group-availability/`,
      payload,
    )
    .then((response: { data: GroupAvailability }) => {
      availability.value = response.data
    })
    .catch((error) => {
      errors.value = getValidationErrorData(error)
    })
}

const debouncedSearchAvailability = debounce(searchAvailability, 600)

watch(
  () => props.modelValue,
  () => {
    selectedDate.value = props.modelValue?.start ?? null
    selectedGroupSize.value = props.modelValue?.group_size ?? 1
  },
  { immediate: true },
)

watch([selectedStart, selectedEnd, selectedGroupSize], () => {
  emit('update:modelValue', {
    start: selectedStart.value,
    end: selectedEnd.value,
    group_size: selectedGroupSize.value,
  })
})

watch(
  state,
  () => {
    debouncedSearchAvailableDates()
  },
  { immediate: true },
)

watchEffect(() => {
  if (selectedDate.value !== null && selectedGroupSize.value > 0) {
    debouncedSearchAvailability()
  }
})

watchEffect(() => {
  emit('availability', availability.value)
})

return (_ctx: any,_cache: any) => {
  const _component_InputErrorMessages = _resolveComponent("InputErrorMessages")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h4", _hoisted_4, _toDisplayString(_ctx.$t('When')) + "?", 1),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(DatePicker, {
            "model-value": selectedDate.value,
            name: "selectedDate",
            errors: _unref(selectedDateErrors),
            class: _normalizeClass(["availability-search__input", {
              'availability-search__input--error':
                _unref(selectedDateErrors).length > 0,
            }]),
            placeholder: _ctx.$t('startDate'),
            "disabled-dates": getPeriodDisabledDates,
            "month-change-on-scroll": false,
            "month-change-on-arrows": false,
            "keep-action-row": true,
            "onUpdate:modelValue": updateSelectedDate,
            onUpdateMonthYear: _unref(onPeriodPickerMonthYearChange)
          }, {
            "action-preview": _withCtx(() => [
              _createElementVNode("div", {
                class: _normalizeClass(["availability-search__dates-loading", {
                  'visibility-hidden': !isAvailableDatesLoading.value,
                }])
              }, [
                _createVNode(LoadingSpinner, { size: 16 }),
                _createElementVNode("span", null, _toDisplayString(_ctx.$t('bookingForm.loadingDatePickerTimeslots')), 1)
              ], 2)
            ]),
            "action-buttons": _withCtx(() => []),
            _: 1
          }, 8, ["model-value", "errors", "class", "placeholder", "onUpdateMonthYear"])
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("h4", _hoisted_7, _toDisplayString(_ctx.$t('howManyAreYou')), 1),
        _createElementVNode("div", _hoisted_8, [
          _createVNode(BaseNumberIncrementer, {
            label: _ctx.$t('bookingWidget.groupSize'),
            amount: selectedGroupSize.value,
            errors: errors.value?.group_size,
            "min-amount": 1,
            disabled: __props.disabled,
            class: "availability-search__input",
            onClickMinus: _cache[0] || (_cache[0] = _withModifiers(() => (selectedGroupSize.value -= 1), ["prevent"])),
            onClickPlus: _cache[1] || (_cache[1] = _withModifiers(() => (selectedGroupSize.value += 1), ["prevent"]))
          }, null, 8, ["label", "amount", "errors", "disabled"])
        ]),
        _createElementVNode("div", null, [
          _createVNode(_component_InputErrorMessages, {
            "model-value": errors.value?.['group_size']
          }, null, 8, ["model-value"])
        ])
      ])
    ])
  ]))
}
}

})